import React, { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Column from '~/Common-Objects/Grids/Column/Column'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Row from '~/Common-Objects/Grids/Row/Row'
import StickyNavBottom from '~/Common-Objects/Navigation/StickNavBottom'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import WarningHolder from '~/Common-Objects/UI/Cards/WarningHolder'
import axios from '../../../../../data/http/axios'
import i18n from '../../../../../i18n'
import { SocialPreview, SupportedApps } from './shared'
import SocialAutomate from './automate'
import moment from 'moment'
import { alterFullpage, alterSiteDrawer } from '../../../../../data/actions/siteActions'
import siteVars from '~/Classes/siteVars'
import postHogFunctions from '~/Classes/posthog'
import StartDate from '../../add/shared/startDate'
import CampaignSendNow from '../../add/steps/step3/sendNow'
import AccountApprovalRequired from '~/Common-Objects/Account/AccountApprovalRequired'
import helpers from '~/Classes/helpers'

function getInitialStartDate(campaignStartDate) {
    const parsed = moment(campaignStartDate)
    if (parsed.isValid()) return parsed.toDate()
    return moment().add(1, 'hour').minute(0).toDate()
}

export default function SocialCampaignCreateSchedule({ campaign, refresh = () => {} }) {
    const account = useSelector((state) => state.accountMaster)
    const history = useHistory()
    const dispatch = useDispatch()
    const [accApps, setAccApps] = useState(null)
    const [startDate, setStartDate] = useState(() => getInitialStartDate(campaign.startDate))
    const [siteTrackers, setSiteTrackers] = useState(null)
    const [error, setError] = useState(null)
    const [tierLimitHit, setTierLimitHit] = useState(false)
    const [approveDrawer, setApproveDrawer] = useState(null)

    const save = async (destination) => {
        await axios.put('/campaign/' + campaign.id, {
            campaign: {
                startDate
            }
        })
        if (destination) history.push(destination)
    }

    const sendNow = () => {
        const modal = (
            <CampaignSendNow
                social={true}
                onConfirm={() => {
                    send(true)
                }}
                onCancel={() => dispatch(alterFullpage(null))}
            />
        )

        dispatch(alterFullpage(modal))
    }

    const send = async (sendImmediately = false) => {
        dispatch(alterFullpage(null))
        try {
            await axios.put('/campaign/' + campaign.id, {
                campaign: {
                    startDate
                }
            })
            await axios.put('/campaign/' + campaign.id + '/start')

            postHogFunctions.capture('social_campaign_started', {
                publish: sendImmediately ? 'immediately' : 'scheduled',
                networks: campaign?.Campaigns?.map((c) => c.options.AppCode)
            })
            history.push('/cp/campaigns')
        } catch (e) {
            let error = e.data.error
            console.log(error)
            if (error.err) {
                error = error.err
                if (error === 'tierOverLimit') {
                    setTierLimitHit(true)
                    error = i18n.t('social:schedule.tierOverLimit')
                } else if (error === 'tierForbids') {
                    setTierLimitHit(true)
                    error = i18n.t('social:schedule.tierForbids')
                }
            }
            setError(error)
        }
    }

    const automate = () => {
        const content = <SocialAutomate CampaignId={campaign.id} campaignName={campaign.campaignName} save={save} />
        dispatch(alterSiteDrawer(true, true, 'right', content, true, 500))
    }

    const toggleLinkTracking = async () => {
        await axios.put('/campaign/' + campaign.id, {
            campaign: {
                options: {
                    ...campaign.options,
                    doNotTrackLinks: !campaign.options.doNotTrackLinks
                }
            }
        })
        await refresh()
    }

    const activateAccount = () => {
        var theDrawer = (
            <NewUserForm
                from='social'
                finish={() => {
                    setApproveDrawer(null)
                }}
            />
        )
        setApproveDrawer(theDrawer)
    }

    useEffect(() => {
        axios.get('/accountMaster/application').then((res) => {
            setAccApps(res.data.Applications)
        })
        axios.get('/accountMaster/tracker').then((res) => {
            setSiteTrackers(res.data.Trackers)
        })
    }, [])

    if (!accApps || !siteTrackers) return <LoadingIcons />

    const links = []
    for (const campaign of campaign.Campaigns) {
        links.push(...helpers.findLinks(campaign.Template.templateJson.content))
    }
    const domains = Array.from(
        new Set(
            links.map(
                (l) => l.replace('https://', '').replace('http://', '').replace('www.', '').split('?')[0].split('/')[0]
            )
        )
    )
    const missingTrackers = domains.filter((d) => !siteTrackers.some((s) => s.host.toLowerCase() === d.toLowerCase()))

    const canAutomate = !missingTrackers.length && !!domains.length
    const automationTooltip = !canAutomate ? i18n.t('social:schedule.automateDisabled') : ''

    let sendDisabled = false
    if (!account.accountMaster.approved) {
        sendDisabled = true
    }

    const sendingNow = moment(startDate) <= moment()

    return (
        <div>
            <Row style={{ marginBottom: 50 }}>
                <Column xs={12} sm={12} md={12} lg={6}>
                    <Paper>
                        <h4 className='mar-b25'>{i18n.t('social:schedule.header')}</h4>
                        <StartDate startDate={startDate} setStartDate={setStartDate} />
                    </Paper>
                    <Paper className='mar-b0'>
                        <h4 className='mar-b15'>{i18n.t('social:schedule.tracking.header')}</h4>
                        {!!domains.length && <p className='mar-b25'>{i18n.t('social:schedule.tracking.domains')}</p>}
                        {!domains.length && <div>{i18n.t('social:schedule.tracking.nodomains')}</div>}

                        {!!missingTrackers.length && (
                            <WarningHolder className='mar-b25'>
                                <div>
                                    <p>{i18n.t('social:schedule.tracking.missing')}</p>
                                    {missingTrackers.map((t) => (
                                        <div className='mar-b5' key={t}>
                                            <strong key={t}>{t}</strong>
                                        </div>
                                    ))}
                                    <Button
                                        className='button-primary mar-t10'
                                        onClick={() => save('/cp/trackers')}
                                    >
                                        {i18n.t('social:schedule.tracking.setup')}
                                    </Button>
                                </div>
                            </WarningHolder>
                        )}

                        {!!domains.length && !missingTrackers.length && (
                            <p className='text-green quickFlex text-heavy mar-b25' style={{ alignItems: 'center' }}>
                                <Icons
                                    className='mar-r10 quickFlex'
                                    style={{ alignItems: 'center' }}
                                    icon='tickCircle'
                                    color='#43b7b2'
                                />
                                {i18n.t('social:schedule.tracking.added')}
                            </p>
                        )}
                        {!!domains.length && (
                            <Toggle
                                after={
                                    !campaign.options.doNotTrackLinks
                                        ? i18n.t('social:schedule.tracking.enabled')
                                        : i18n.t('social:schedule.tracking.disabled')
                                }
                                checked={!campaign.options.doNotTrackLinks}
                                onChange={toggleLinkTracking}
                            />
                        )}
                    </Paper>
                </Column>
                <Column xs={12} sm={12} md={12} lg={6}>
                    <Carousel campaign={campaign} campaigns={campaign.Campaigns} startDate={startDate} />
                </Column>
            </Row>
            <StickyNavBottom>
                <div className='quickFlex' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className='quickFlex' style={{ alignItems: 'center' }}>
                        <Button
                            iconLeft={<Icons icon='arrowLeft' />}
                            className={'button-primary mar-r10'}
                            onClick={() => save(`/cp/campaigns/add/social/${campaign.id}/3`)}
                        >
                            {i18n.t('social:common.back')}
                        </Button>
                        <Button onClick={() => save('/cp/campaigns')}>
                            {i18n.t('campaigns:standard.step3.save')}
                        </Button>
                    </div>
                    <AccountApprovalRequired
                        messageOrientation='left'
                        notApprovedText={i18n.t('social:schedule.notApproved')}
                        inReviewText={i18n.t('social:schedule.pendingApproval')}
                        activateFrom={'campaign'}
                        approvedAction={
                            <div className='quickFlex' style={{ alignItems: 'center' }}>
                                {!!error && <div className='mar-r10 text-red text-heavy'>{error}</div>}
                                {tierLimitHit && (
                                    <Button
                                        className='mar-r10'
                                        onClick={() => history.push('/cp/company/subscription')}
                                    >
                                        Upgrade
                                    </Button>
                                )}
                                {!tierLimitHit && (
                                    <>
                                        <Button
                                            className='mar-r10'
                                            disabled={!canAutomate}
                                            tooltip={automationTooltip}
                                            for='socialAutomate'
                                            iconRight={<Icons icon='lightning' />}
                                            onClick={automate}
                                        >
                                            {i18n.t('social:schedule.automate')}
                                        </Button>
                                        <Button
                                            className='button-primary'
                                            iconRight={<Icons icon='share' />}
                                            onClick={sendingNow ? sendNow : send}
                                        >
                                            {!sendingNow
                                                ? i18n.t('social:schedule.schedule')
                                                : i18n.t('social:schedule.send')}
                                        </Button>
                                    </>
                                )}
                            </div>
                        }
                    />
                </div>
            </StickyNavBottom>
            {approveDrawer}
        </div>
    )
}

function Carousel({ campaign, campaigns, startDate }) {
    const [index, setIndex] = useState(0)
    const interval = useRef()
    const preview = useRef(null)

    useEffect(() => {
        interval.current = setInterval(() => {
            setIndex((index + 1) % campaigns.length)
        }, 5000)

        return () => clearInterval(interval.current)
    }, [index, campaigns])

    const left = () => {
        clearInterval(interval.current)
        setIndex(index === 0 ? campaigns.length - 1 : index - 1)
    }

    const right = () => {
        clearInterval(interval.current)
        setIndex(index === campaigns.length - 1 ? 0 : index + 1)
    }

    return (
        <div
            className='carousel col-lg-height-100'
            style={{ minHeight: preview?.current ? preview.current.offsetHeight + 300 : 600 }}
        >
            <div className='carousel-item-arrow-left'>
                <Icons icon='arrowLeft' onClick={left} />
            </div>
            <Paper className='paper-grey col-lg-height-100'>
                {campaigns.map((subCampaign, i) => {
                    const app = SupportedApps.find((a) => a.code === subCampaign.options.AppCode)
                    return (
                        <div
                            className='carousel-item'
                            key={subCampaign.id}
                            style={{
                                transform: `translateX(${100 * (i - index)}%)`
                            }}
                        >
                            <div
                                className='quickFlex'
                                style={{
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: 32,
                                    marginTop: 32
                                }}
                            >
                                <Icons icon={app.icon} style={{ height: 30, width: 30 }} disabled={true} />
                            </div>

                            {/* <h4 style={{ textAlign: 'center', marginBottom: 64, marginTop: 32 }}>{i18n.t('social:channels.' + app.code)}</h4> */}
                            <SocialPreview
                                ref={preview}
                                video={!!campaign?.Video}
                                SocialAccountId={subCampaign.options.SocialAccountId}
                                text={subCampaign.Template.templateJson.content}
                                images={
                                    campaign.Video
                                        ? [siteVars.repoEndPoint + campaign.Video.thumb]
                                        : subCampaign.Template.templateJson.images
                                }
                                startDate={startDate}
                                provider={app}
                                warning={false}
                            />
                        </div>
                    )
                })}
            </Paper>
            <div className='carousel-item-arrow-right'>
                <Icons icon='arrowRight' onClick={right} />
            </div>
        </div>
    )
}
